// The way back into delivery setup for someone who closed the tab partway.
//
// THE HOLE THIS FILLS. A subscriber left at status='onboarding' had exactly one
// route back into SetupFlow: the original invitation email. Nothing in the
// member portal mentioned it. Someone who quits at the property-access step
// needs an email or a phone call to recover, and most simply will not — which
// is the signup funnel leaking at its last mile.
//
// The backend already handled resume: OnboardingStateView returns next_step and
// SetupFlow reads it to land on the right screen. Only the surfacing was
// missing, so this component is deliberately thin — it asks where the customer
// is and links there.
//
// WHY A BANNER AND NOT A REDIRECT. A redirect on login hijacks someone who came
// in to check their points or upload a receipt, and onboarding is not urgent
// enough to hold their account hostage. It would also fight BridgeClubLogin's
// own ?redirect= handling. A banner is present on every page until the work is
// done, and ignorable on any of them.
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { deliveryAPI, OnboardingState } from '../../services/deliveryAPI';

// The setup flow itself. Showing a "resume setup" banner on top of setup would
// be absurd, and the route is already the destination.
const SETUP_ROUTE = '/bridge-club/onboarding';

const S: Record<string, React.CSSProperties> = {
  wrap: {
    background: '#fffbeb',
    borderBottom: '1px solid #fcd34d',
    padding: '12px 16px',
  },
  inner: {
    maxWidth: '1100px',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
  },
  text: { flex: '1 1 260px', minWidth: 0, fontSize: '14px', color: '#78350f' },
  strong: { fontWeight: 700, display: 'block', marginBottom: '2px' },
  next: { color: '#92400e' },
  button: {
    background: '#059669',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    minHeight: '44px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};

const OnboardingResumeBanner: React.FC = () => {
  const [state, setState] = useState<OnboardingState | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    deliveryAPI
      .getOnboarding()
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch(() => {
        // A member who is not a delivery subscriber 404s here, and that is the
        // NORMAL case for most of the Bridge Club. Swallow it and render
        // nothing rather than showing an error to someone who never signed up
        // for delivery in the first place.
        if (!cancelled) setState(null);
      });
    return () => {
      cancelled = true;
    };
    // Re-checked on navigation so the banner disappears as soon as the customer
    // finishes a step and comes back out, without a full reload.
  }, [location.pathname]);

  if (!state) return null;
  if (state.complete || state.status !== 'onboarding') return null;
  if (location.pathname.startsWith(SETUP_ROUTE)) return null;

  return (
    <div style={S.wrap} role="status">
      <div style={S.inner}>
        <div style={S.text}>
          <span style={S.strong}>Your delivery setup isn&apos;t finished.</span>
          {state.next_step_label ? (
            <span style={S.next}>Next: {state.next_step_label}.</span>
          ) : (
            <span style={S.next}>Pick up where you left off.</span>
          )}
        </div>
        <button
          type="button"
          style={S.button}
          onClick={() => navigate(SETUP_ROUTE)}
        >
          Finish setup
        </button>
      </div>
    </div>
  );
};

export default OnboardingResumeBanner;
